import { observable, action, computed, decorate } from 'mobx'
import { getFormSlug } from '../../formState'
import logError from '../../../../../helpers/errorLogger'
import { arrayMove } from 'react-sortable-hoc'
import { generalFormError } from '../../../../../helpers/errorMessages'
import generateNewField from './fieldLevelFormGenerator'
import { updateFieldsPositions } from 'api/api'
import {
  mutativeRemoveFromArray,
  notify,
  createFormData,
  createJsonData,
  flattenArray,
  withFinally,
  omit,
} from '../../../../../helpers/generalHelpers'
import mainStore from '../../../../../stores/mainStore'
import { getRawMessage } from '../../../../../locale'
import { specificFieldDictionary } from '../FormFields/SingleField'
import { FIELD_TYPE } from 'types'
import { services } from 'api/services'

class fieldsManagerClass {
  fieldsArray = []

  focusedFieldId = null

  fieldActionLoading = false

  choiceShuffleResetFlag = false

  toggleChoicesShuffle = (status) => {
    this.choiceShuffleResetFlag = status
  }

  isFieldActionLoading = (boolean) => {
    this.fieldActionLoading = boolean
  }

  reset = () => {
    this.fieldsArray = []
    this.focusedFieldId = null
  }

  fieldHasGainedFocus = (fieldId, disableServerSyncing) => {
    // double save the previous focused field, then update focused field id
    if (
      !disableServerSyncing &&
      this.focusedFieldId &&
      this.focusedFieldId !== fieldId
    ) {
      this.saveSingleFieldToServer(this.focusedFieldId)
    }
    this.focusedFieldId = fieldId
  }

  fieldHasLostFocus = () => {
    this.saveSingleFieldToServer(this.focusedFieldId)
    this.focusedFieldId = null
  }

  generateNewFieldData = (field) => {
    const blackListData = ['created_at', 'updated_at', 'slug']
    let fieldOption = omit(blackListData)(field)

    if (field.choice_groups) {
      fieldOption = {
        ...fieldOption,
        choice_groups: field.choice_groups.map((choice) => ({
          ...omit(blackListData)(choice),
        })),
      }
    }
    if (field.choice_items) {
      fieldOption = {
        ...fieldOption,
        choice_items: field.choice_items.map((choice) => ({
          ...omit(blackListData)(choice),
        })),
      }
    }
    if (field.column_groups) {
      fieldOption = {
        ...fieldOption,
        column_groups: field.column_groups.map((choice) => ({
          ...omit(blackListData)(choice),
        })),
      }
    }
    return fieldOption
  }

  get anyFieldHasFocus() {
    return this.focusedFieldId !== null
  }

  get currentFocusedField() {
    return this.anyFieldHasFocus
      ? this.findFieldFormById(this.focusedFieldId)
      : null
  }

  get fieldsCount() {
    return this.fieldsArray.length
  }

  get hasAnyFields() {
    return this.fieldsArray.length > 0
  }

  get positionOfFocusedField() {
    return this.currentFocusedField
      ? this.readPropertyOf(this.currentFocusedField, 'position')
      : ''
  }

  get orderOfFieldByArrayIndex() {
    return this.currentFocusedField
      ? this.findIndexOfFieldForm(this.focusedFieldId) + 1
      : ''
  }

  addField = (
    fieldType,
    options = {}, // used for required options or default values on creating the new field
    initialValues = {}, // initialized the existed field values (on loading the form or clicking on existed field)
    config = { shouldSyncWithServer: true, autoFocus: true }
  ) => {
    const { form, id } = generateNewField(fieldType, initialValues, options)

    this.fieldsArray.push(form)

    if (config.autoFocus) {
      this.fieldHasGainedFocus(id)
    }

    if (config.shouldSyncWithServer) {
      this.syncWithServer({
        payload: { type: fieldType, ...options },
        actionType: 'NEW_FIELD',
        fieldId: id,
      })
    }
  }
  duplicateField = (
    fieldType,
    options = {},
    parentIndex,
    config = { shouldSyncWithServer: true, autoFocus: true }
  ) => {
    const fieldOption = this.generateNewFieldData(options)

    const { form, id } = generateNewField(fieldType, fieldOption, fieldOption)

    this.fieldsArray.splice(parentIndex + 1, 0, form)

    const disablingLostFocusSyncing = true

    if (config.autoFocus) {
      this.fieldHasGainedFocus(id, disablingLostFocusSyncing)
    }

    if (config.shouldSyncWithServer) {
      this.syncWithServer({
        payload: { type: fieldType, ...options },
        actionType: 'DUPLICATE_FIELD',
        fieldId: id,
        fieldSlug: options.slug,
      })
    }
  }

  addFieldAtIndex = (fieldType, atIndex, options = {}) => {
    const { form, id } = generateNewField(fieldType, {}, options)

    this.fieldsArray.splice(atIndex, 0, form)

    this.fieldHasGainedFocus(id)
    this.syncWithServer({
      payload: { type: fieldType, ...options },
      actionType: 'NEW_FIELD',
      fieldId: id,
    })
  }

  removeFieldById = (fieldId) => {
    this.syncWithServer({ actionType: 'REMOVE_FIELD', fieldId })
  }

  valuesToFilter = ['current_value', 'id']

  valuesToFilterToCheckFieldPristine = [
    ...this.valuesToFilter,
    'slug',
    'field_type',
    'position',
  ]

  fieldNameToRenameRegEx = /(_selector|_creator)$/

  getReadOnlyFields = (formField) => {
    const fieldType = formField.$('field_type').value
    return specificFieldDictionary[fieldType]
      ? specificFieldDictionary[fieldType].filter((field) => field.readOnly)
      : []
  }

  getFieldsWithAttribute = (formField) => (attributeName) => {
    const fieldType = formField.$('field_type').value
    return specificFieldDictionary[fieldType]
      ? specificFieldDictionary[fieldType].filter(
          (field) => field[attributeName]
        )
      : []
  }

  getAlwaysSendFields = (formField) => {
    const fieldType = formField.$('field_type').value

    return specificFieldDictionary[fieldType]
      ? specificFieldDictionary[fieldType]
          .filter((field) => field.alwaysSend)
          .map((field) => field.propName)
      : []
  }

  removeReadOnlyValuesOfField = (field) => {
    // readonly fields generated by server and it shouldn't change from client-side
    const readOnlyFields = this.getReadOnlyFields(field)

    if (readOnlyFields.length > 0) {
      const readOnlyKeyNames = readOnlyFields.map((field) => field.propName)
      return omit(readOnlyKeyNames)(field.values())
    }
    return field.values()
  }

  updateReadOnlyFieldsWithServerResponse = (formField, { data }) => {
    if (formField && data && data.field) {
      const readOnlyFields = this.getReadOnlyFields(formField)
      if (readOnlyFields.length > 0) {
        readOnlyFields.forEach((field) =>
          formField
            .$(field.fieldNameInFormField)
            .onChange(data.field[field.propName])
        )
      }
    }
  }

  updateUpdatableFieldsWithServerResponse = (formField, { data }) => {
    if (formField && data && data.field) {
      const updatableFields =
        this.getFieldsWithAttribute(formField)('updatable')
      if (updatableFields.length > 0) {
        updatableFields.forEach((field) => {
          const fieldProps = formField.$(field.fieldNameInFormField)
          const fieldValue =
            typeof fieldProps.$input === 'function'
              ? fieldProps.$input(data.field[field.updateFrom])
              : data.field[field.updateFrom]

          fieldProps.onChange(fieldValue)
        })
      }

      const fieldsToClear =
        this.getFieldsWithAttribute(formField)('clearOnUpdate')
      if (fieldsToClear.length > 0) {
        fieldsToClear.forEach((field) =>
          formField.$(field.fieldNameInFormField).onChange('')
        )
      }
    }
  }

  /*
    NOTE: when a field loses focus this function is called but
    it is called for every one of the fields, it get fieldId which
    is the last active field id
 */
  saveSingleFieldToServer = async (fieldId) => {
    const formField = fieldId ? this.findFieldFormById(fieldId) : null

    if (formField) {
      // can be null, when server does not accept the creation on field
      // validate the fieldForm first to give user immediate feedback
      await formField.validate()
      formField.showErrors(true)

      if (formField.isValid) {
        const fieldValues = this.removeReadOnlyValuesOfField(formField)
        const alwaysSendFields = this.getAlwaysSendFields(formField)

        const cleanValues = {
          form: getFormSlug(),
          ...this.filterAndRenameValues(
            fieldValues,
            this.valuesToFilter,
            this.fieldNameToRenameRegEx
          ),
        }

        const dataToSubmit = {
          actionType: 'UPDATE_FORM_FIELD_DATA',
          // some fields contain base64 images, and due to back-end request, the data should send as JSON
          payload: createJsonData(cleanValues, {
            alwaysSend: [
              'description',
              'title',
              'alias',
              'required',
              'admin_only',
              'is_random_sortable',
              ...alwaysSendFields,
            ],
          }),
          fieldSlug: fieldValues.slug,
        }

        this.syncWithServer(dataToSubmit)
          .then(this.handleFieldFormUpdateResById(fieldId, formField))
          .catch(this.invalidateFieldFormById(fieldId))
      }
    }
  }

  handleFieldFormUpdateResById = (fieldId, formField) => {
    return this.withSyncEnd((response) => {
      if (response) {
        if (response.status < 400) {
          this.updateReadOnlyFieldsWithServerResponse(formField, response.data)
          this.updateUpdatableFieldsWithServerResponse(formField, response.data)
          return
        }
        const { form_errors, general_errors } = response.data.errors
        if (general_errors)
          notify(flattenArray([generalFormError, ...general_errors]), 'error')

        this.invalidateFormFieldFromWithErrorsObject(
          this.findFieldFormById(fieldId),
          form_errors
        )
      }
    })
  }

  reloadFieldData = (fieldSlug, payload) => {
    this.syncWithServer({
      actionType: 'RELOAD_FORM_FIELD_DATA',
      fieldSlug,
      payload,
    })
  }

  handleReloadFieldData = async (fieldSlug, payload = {}) => {
    const { onSuccess, onFailure } = payload

    const [err, res] = await services.field.getOne(fieldSlug)

    if (res) {
      const fieldForm = this.findFieldFormBySlug(fieldSlug)

      fieldForm.each((field) => {
        const fieldName = this.fieldNameAdapter(field.name)

        const fieldProps = fieldForm.$(field.name)

        if (!this.valuesToFilterToCheckFieldPristine.includes(field.name)) {
          const fieldValue =
            typeof fieldProps.$input === 'function'
              ? fieldProps.$input(res.field[fieldName])
              : res.field[fieldName]

          fieldProps.onChange(fieldValue)
        }
      })
    } else if (err) {
      // catch
      const { form_errors, general_errors } = err

      notify(flattenArray([generalFormError, ...general_errors]), 'error')

      this.invalidateFormFieldFromWithErrorsObject(
        this.findFieldFormBySlug(fieldSlug),
        form_errors
      )
    }

    // finally
    this.syncEnded()

    if (res) {
      typeof onSuccess === 'function' && onSuccess(res)
    } else if (err) {
      typeof onFailure === 'function' && onFailure(err)
    }
  }

  isFormPristine = (formFields) => {
    let isPristine = true

    formFields.each((field) => {
      if (!this.valuesToFilterToCheckFieldPristine.includes(field.name)) {
        if (!field.isPristine) {
          isPristine = false
        }
      }
    })

    return isPristine
  }

  filterAndRenameValues = (
    valuesObject,
    filterList,
    fieldNameToRenameRegEx
  ) => {
    const result = {}

    for (const key in valuesObject) {
      if (valuesObject.hasOwnProperty(key)) {
        if (!filterList.includes(key)) {
          if (this.fieldNameToRenameRegEx.test(key)) {
            result[key.replace(this.fieldNameToRenameRegEx, '')] =
              valuesObject[key]
          } else {
            result[key] = valuesObject[key]
          }
        }
      }
    }

    return result
  }

  syncStarted = () => {
    mainStore.formMakerIsSavingStart()
  }

  syncEnded = () => {
    mainStore.formMakerIsSavingEnd()
  }

  withSyncEnd = withFinally(this.syncEnded)

  syncWithServer = ({ actionType, payload, fieldId, fieldSlug }) => {
    this.syncStarted()

    switch (actionType) {
      case 'NEW_FIELD':
        this.submitNewFieldToServer(payload)
          .then(this.newFieldServerSubmitSuccess(fieldId))
          .catch(this.newFieldServerSubmissionError)
        return

      case 'DUPLICATE_FIELD':
        this.submitDuplicateFieldToServer(fieldSlug)
          .then(this.DuplicatedFieldServerSubmitSuccess(fieldId))
          .catch(this.newFieldServerSubmissionError)
        return

      case 'REMOVE_FIELD':
        this.requestFieldRemoval(this.getFieldSlugById(fieldId))
          .then(this.removeFieldServerSubmitSuccess(fieldId))
          .catch(this.removeFieldServerSubmissionError)
        return

      case 'RE_POSITION':
        return updateFieldsPositions(getFormSlug(), createFormData(payload))

      case 'UPDATE_FORM_FIELD_DATA':
        return services.field.edit(fieldSlug, payload)

      case 'RELOAD_FORM_FIELD_DATA':
        return this.handleReloadFieldData(fieldSlug, payload)

      default:
        console.warn('No action was passed to syncWithServer')
        this.syncEnded()
        return
    }
  }

  newFieldServerSubmitSuccess = (fieldId) =>
    this.withSyncEnd((slug) => {
      // this.syncEnded()
      this.findFieldFormAndUpdate(fieldId, 'slug', slug, false)
      this.updateFieldPosition(fieldId, false)
    })

  DuplicatedFieldServerSubmitSuccess = (fieldId) =>
    this.withSyncEnd((slug) => {
      this.findFieldFormAndUpdate(fieldId, 'slug', slug, true)
      this.updateFieldPosition(fieldId, true)
    })

  newFieldServerSubmissionError = this.withSyncEnd((error) => {
    logError(error)
    this.removeFieldFormByIndex(this.fieldsCount - 1)
    notify(...submissionError)
  })

  removeFieldServerSubmitSuccess = (fieldId) =>
    this.withSyncEnd(() => {
      // this.syncEnded()
      this.removeFieldFormByIndex(this.findIndexOfFieldForm(fieldId))
      this.reCalculateFieldFormsPositions()
    })

  removeFieldServerSubmissionError = this.withSyncEnd((err) => {
    // this.syncEnded()
    logError(err)
    notify(...removeError)
  })

  removeFieldFormByIndex = (index) => {
    mutativeRemoveFromArray(this.fieldsArray, index)
  }

  submitNewFieldToServer = async (fieldData) => {
    const [err, res] = await services.field.create(getFormSlug(), fieldData)
    return err ? Promise.reject(err) : Promise.resolve(res.field.slug)
  }
  submitDuplicateFieldToServer = async (fieldData) => {
    const [err, res] = await services.field.copy(fieldData)
    if (err) return Promise.reject(err)
    return Promise.resolve(res.field.slug)
  }
  requestFieldRemoval = async (fieldSlug) => {
    this.isFieldActionLoading(true)
    const [err, res] = await services.field.delete(fieldSlug, {
      remove_related_form_logic: true,
    })
    // remove the field and its logic from the form
    if (err) {
      this.isFieldActionLoading(false)
      return Promise.reject(err)
    }
    this.isFieldActionLoading(false)
    return Promise.resolve(res)
  }

  findFieldFormAndUpdate = (fieldFormId, property, newValue, isDuplicate) => {
    const indexInFieldsArray = this.findIndexOfFieldForm(fieldFormId)
    // !isDuplicate && this.fieldsArray[indexInFieldsArray].$('title').onChange('') // erasing initial title
    this.fieldsArray[indexInFieldsArray].$(property).onChange(newValue)
  }

  updateFieldPosition = (id, isDuplicate) => {
    const indexInFieldFormsArray = this.findIndexOfFieldForm(id)
    this.fieldsArray[indexInFieldFormsArray]
      .$('position')
      .onChange(indexInFieldFormsArray + 1)

    if (isDuplicate) {
      this.reCalculateFieldFormsPositions()
      this.syncWithServer({
        actionType: 'RE_POSITION',
        payload: { form_fields: this.aggregateAllSlugs() },
      })
        .then(() => this.syncEnded())
        .catch((err) => {
          logError(err)
          notify(...positionUpdateError)
          this.syncEnded()
        })
    }
  }

  findFieldFormById = (id) => this.fieldsArray[this.findIndexOfFieldForm(id)]

  findFieldFormBySlug = (slug) => {
    const formField = this.fieldsArray.find(
      (formField) => this.readPropertyOf(formField, 'slug') === slug
    )

    return formField || null
  }

  findIndexOfFieldForm = (id) =>
    this.fieldsArray.findIndex(
      (field) => id === this.readPropertyOf(field, 'id')
    )

  getFieldSlugById = (id) => {
    const field = this.findFieldFormById(id)
    return field ? this.readPropertyOf(field, 'slug') : null
  }

  reCalculateFieldFormsPositions = () => {
    this.fieldsArray.forEach((fieldForm, index) =>
      fieldForm.$('position').onChange(index + 1)
    )
  }

  handleSingleReposition = (oldIndex, newIndex) => {
    /*
      when an item is repositioned,
      it's old and new position is passed to this function
    */

    // if it is not really moved, ignore it
    if (oldIndex === newIndex) return

    // change place in state
    this.fieldsArray = arrayMove(this.fieldsArray, oldIndex, newIndex)

    // set positions again for all field (single reposition can change all positions)
    this.reCalculateFieldFormsPositions()

    // notify the server about new positions
    this.syncWithServer({
      actionType: 'RE_POSITION',
      payload: { form_fields: this.aggregateAllSlugs() },
    })
      .then(() => this.syncEnded())
      .catch((err) => {
        logError(err)
        notify(...positionUpdateError)
        this.syncEnded()
      })
  }

  readPropertyOf = (field, prop) => field.$(prop).$value

  addFieldsFromTemplate = (template) => {
    const { fields: templateFields } = template

    for (const key in templateFields) {
      if (templateFields.hasOwnProperty(key)) {
        const theField = templateFields[key]
        // Remove the fields that have their is_editable property set to false; like grade variable
        if ([null, undefined, true].includes(theField.is_editable)) {
          this.addField(theField.type, {}, theField, {
            shouldSyncWithServer: false,
            autoFocus: false,
          })
        }
      }
    }

    this.fieldHasLostFocus(null)
    this.sortAllFormFieldsByTheirPosition()
  }

  sortAllFormFieldsByTheirPosition = () => {
    // this should be done in a webworker in future

    this.fieldsArray = this.fieldsArray.toJS().sort((formA, formB) => {
      const positionOfA = this.readPropertyOf(formA, 'position')
      const positionOfB = this.readPropertyOf(formB, 'position')

      if (!positionOfA || !positionOfB) return 1

      if (parseInt(positionOfA, 10) >= parseInt(positionOfB, 10)) {
        return 1
      } else {
        return -1
      }
    })
  }
  validateAllFields = () =>
    /* validate method on forms return a promise */
    ({
      validationPromise: Promise.all(
        this.fieldsArray.map((fieldForm) => {
          fieldForm.showErrors(true)
          return fieldForm.validate()
        })
      ),
      isThereAFieldWithError: () =>
        this.fieldsArray.some((formField) => !formField.isValid),
    })

  showAllErrors = () => {
    this.fieldsArray.forEach((fieldForm) => {
      fieldForm.showErrors()
    })
  }

  aggregateAllSlugs = () =>
    this.fieldsArray.map((fieldForm) => this.readPropertyOf(fieldForm, 'slug'))

  invalidateGroupOfFields = (fieldErrorsObject) => {
    if (typeof fieldErrorsObject !== 'object') {
      throw new Error(
        `formFieldManager.invalidateGroupOfFields expects an Object
         but received ${typeof fieldErrorsObject} ${JSON.stringify(
          fieldErrorsObject
        )}`
      )
    }

    for (const formFieldSlug in fieldErrorsObject) {
      if (fieldErrorsObject.hasOwnProperty(formFieldSlug)) {
        this.invalidateFormBySlug(formFieldSlug)(
          fieldErrorsObject[formFieldSlug]
        )
      }
    }
  }

  invalidateFieldFormById = (fieldId) =>
    this.withSyncEnd((errors) => {
      this.invalidateField(this.findFieldFormById(fieldId), errors)
    })

  invalidateFormBySlug = (formFieldSlug) => (errors) => {
    this.invalidateField(this.findFieldFormBySlug(formFieldSlug), errors)
  }

  invalidateField = (fieldForm, errors) => {
    if (!fieldForm || !errors) {
      return
    }

    if (typeof errors === 'object' && !Array.isArray(errors)) {
      this.invalidateFormFieldFromWithErrorsObject(fieldForm, errors)
    } else if (Array.isArray(errors)) {
      errors = errors.reduce((acc, current) => `${acc}\n${current}`, '')
    } else if (typeof errors !== 'string') {
      errors = this.fallbackFormFieldErrorMessage
    }
    fieldForm.invalidate(errors)
    fieldForm.showErrors(true)
  }

  fallbackFormFieldErrorMessage = getRawMessage('general.field.error.message')

  invalidateFormFieldFromWithErrorsObject(fieldForm, errors) {
    const err = errors.form_errors || errors
    // loop over all fields in the form, if their name exists in errors, invalidate them
    fieldForm.each((field) => {
      const fieldName = this.fieldNameAdapter(field.name)
      if (err[fieldName]) {
        field.invalidate(err[fieldName])
        field.showErrors(true)
      }
    })

    fieldForm.showErrors(true)
  }

  fieldNameAdapter = (fieldName) => {
    if (this.fieldNameToRenameRegEx.test(fieldName)) {
      return fieldName.replace(this.fieldNameToRenameRegEx, '')
    }

    return fieldName
  }

  get isThereAnEmailField() {
    return this.fieldsArray.some((fieldForm) => {
      const fieldType = this.readPropertyOf(fieldForm, 'field_type')
      return [FIELD_TYPE.EMAIL, FIELD_TYPE.EMAIL_VERIFICATION].includes(
        fieldType
      )
    })
  }
}

decorate(fieldsManagerClass, {
  fieldsArray: observable,
  focusedFieldId: observable,
  fieldActionLoading: observable,
  choiceShuffleResetFlag: observable,
  toggleChoicesShuffle: action,
  isFieldActionLoading: action,
  reset: action,
  fieldHasGainedFocus: action,
  fieldHasLostFocus: action,
  generateNewFieldData: action,
  addField: action,
  duplicateField: action,
  addFieldAtIndex: action,
  removeFieldById: action,
  syncWithServer: action,
  newFieldServerSubmitSuccess: action,
  DuplicatedFieldServerSubmitSuccess: action,
  newFieldServerSubmissionError: action,
  removeFieldServerSubmitSuccess: action,
  removeFieldServerSubmissionError: action,
  addFieldsFromTemplate: action,
  validateAllFields: action,
  showAllErrors: action,
  reloadFieldData: action,
  anyFieldHasFocus: computed,
  currentFocusedField: computed,
  fieldsCount: computed,
  hasAnyFields: computed,
  positionOfFocusedField: computed,
  isThereAnEmailField: computed,
  orderOfFieldByArrayIndex: computed,
})

const submissionError = [
  getRawMessage('general.error.submissionError'),
  'error',
]

const removeError = [
  getRawMessage('general.field.removeTimeout.error.message'),
  'error',
]

const positionUpdateError = [
  getRawMessage('pages.formMaker.messages.errors.reorderFieldsTimeoutError'),
  'error',
]
const fieldManagerStore = new fieldsManagerClass()

export default fieldManagerStore
